import { Arrays, asInstanceOf, assert, Attempts, clamp, isAbsent, isDefined, isInstanceOf, Option, panic, quantizeRound, Strings, UUID } from "@opendaw/lib-std";
import { PPQN } from "@opendaw/lib-dsp";
import { IndexedBox, PointerField } from "@opendaw/lib-box";
import { AudioUnitType, Pointers } from "@opendaw/studio-enums";
import { AudioUnitBox, CaptureAudioBox, CaptureMidiBox, InstrumentCompositeBox, InstrumentCompositeCellBox, NoteClipBox, NoteEventBox, NoteEventCollectionBox, NoteRegionBox, TrackBox, ValueClipBox, ValueEventBox, ValueEventCollectionBox, ValueRegionBox } from "@opendaw/studio-boxes";
import { AudioUnitBoxAdapter, AudioUnitFactory, ColorCodes, DeviceAccepts, DeviceBoxUtils, InstrumentFactories, InterpolationFieldAdapter, isModulatorBox, ProjectQueries, RegionAdapters, RegionOverlap, TrackBoxAdapter, TrackType, TransferUtils } from "@opendaw/studio-adapters";
import { ProjectModulation } from "./ProjectModulation";
import { AudioContentFactory } from "./audio";
import { NoteMidiExport } from "./NoteMidiExport";
import { AudioWavExport } from "./AudioWavExport";
import { AudioUnitAsLayer } from "./AudioUnitAsLayer";
import { BoxGraphCopy } from "../BoxGraphCopy";
import { DevicesClipboard } from "../ui/clipboard/types/DevicesClipboardHandler";
// noinspection JSUnusedGlobalSymbols
export class ProjectApi {
    modulation;
    #project;
    constructor(project) {
        this.#project = project;
        this.modulation = new ProjectModulation(project);
    }
    setBpm(value) {
        if (isNaN(value)) {
            return;
        }
        this.#project.timelineBoxAdapter.box.bpm.setValue(clamp(value, 30, 1000));
    }
    catchupAndSubscribeBpm(observer) {
        return this.#project.timelineBoxAdapter.box.bpm.catchupAndSubscribe(owner => observer(owner.getValue()));
    }
    catchupAndSubscribeAudioUnits(listener) {
        return this.#project.rootBoxAdapter.audioUnits.catchupAndSubscribe(listener);
    }
    createInstrument({ create, defaultIcon, defaultName, trackType }, options = {}) {
        const { name, icon, index } = options;
        const { boxGraph, rootBox, userEditingManager } = this.#project;
        assert(rootBox.isAttached(), "rootBox not attached");
        const existingNames = ProjectQueries.existingInstrumentNames(rootBox);
        const audioUnitBox = AudioUnitFactory.create(this.#project.skeleton, AudioUnitType.Instrument, this.#trackTypeToCapture(boxGraph, trackType), index);
        const uniqueName = Strings.getUniqueName(existingNames, name ?? defaultName);
        const iconSymbol = icon ?? defaultIcon;
        const instrumentBox = create(boxGraph, audioUnitBox.input, uniqueName, iconSymbol, options.attachment);
        const trackBox = TrackBox.create(boxGraph, UUID.generate(), box => {
            box.index.setValue(0);
            box.type.setValue(trackType);
            box.tracks.refer(audioUnitBox.tracks);
            box.target.refer(audioUnitBox);
        });
        userEditingManager.audioUnit.edit(audioUnitBox.editing);
        return { audioUnitBox, instrumentBox, trackBox };
    }
    createAnyInstrument(factory) {
        return this.createInstrument(factory);
    }
    // Moves a unit to `slot`, an index among the units BEFORE the move (as a drop between two units names it).
    placeAudioUnit(audioUnitBox, slot) {
        const start = audioUnitBox.index.getValue();
        const delta = slot - start;
        if (delta < 0 || delta > 1) {
            IndexedBox.moveIndex(this.#project.rootBox.audioUnits, start, delta);
        }
    }
    // A drop names the unit it lands BEFORE, not an index: resolved when the new unit exists, which may be after
    // an async load. No anchor, or one deleted meanwhile, leaves the unit where it was created.
    placeAudioUnitBefore(audioUnitBox, anchor) {
        anchor.flatMap(uuid => this.audioUnitIndex(uuid)).ifSome(slot => this.placeAudioUnit(audioUnitBox, slot));
    }
    audioUnitIndex(uuid) {
        return this.#project.boxGraph.findBox(uuid)
            .map(box => asInstanceOf(box, AudioUnitBox).index.getValue());
    }
    replaceMIDIInstrument(target, fromFactory, attachment) {
        const replacedInstrumentName = target.label.getValue();
        const hostBox = target.host.targetVertex.unwrap("Is not connect to AudioUnitBox").box;
        const audioUnitBox = asInstanceOf(hostBox, AudioUnitBox);
        if (audioUnitBox.type.getValue() !== AudioUnitType.Instrument) {
            return Attempts.err("AudioUnitBox does not hold an instrument");
        }
        const captureBox = audioUnitBox.capture.targetVertex.unwrap("AudioUnitBox does not hold a capture").box;
        if (!isInstanceOf(captureBox, CaptureMidiBox)) {
            return Attempts.err("Cannot replace instrument without CaptureMidiBox");
        }
        if (fromFactory.trackType !== TrackType.Notes) {
            return Attempts.err("Cannot replace instrument with track type " + TrackType[fromFactory.trackType] + "");
        }
        console.debug(`Replace instrument '${replacedInstrumentName}' with ${fromFactory.defaultName}`);
        target.delete();
        const { boxGraph } = this.#project;
        const { create, defaultIcon, defaultName } = fromFactory;
        return Attempts.ok(create(boxGraph, audioUnitBox.input, defaultName, defaultIcon, attachment));
    }
    createCompositeLayer(composite, factory, attachment, atIndex = Number.MAX_SAFE_INTEGER) {
        if (!InstrumentFactories.isLayerInstrument(factory)) {
            return Attempts.err(`${factory.defaultName} cannot be used as a layer`);
        }
        const { boxGraph } = this.#project;
        const { create, defaultIcon, defaultName } = factory;
        const layers = IndexedBox.collectIndexedBoxes(composite.cells);
        const index = clamp(atIndex, 0, layers.length);
        layers.slice(index).forEach((box, offset) => box.index.setValue(index + offset + 1));
        const cellBox = InstrumentCompositeCellBox.create(boxGraph, UUID.generate(), box => {
            box.composite.refer(composite.cells);
            box.index.setValue(index);
        });
        return Attempts.ok({ cellBox, instrumentBox: create(boxGraph, cellBox.instrument, defaultName, defaultIcon, attachment) });
    }
    setLayerInstrument(cellBox, factory, attachment) {
        if (!InstrumentFactories.isLayerInstrument(factory)) {
            return Attempts.err(`${factory.defaultName} cannot be used as a layer`);
        }
        cellBox.instrument.pointerHub.incoming().forEach(pointer => pointer.box.delete());
        const { create, defaultIcon, defaultName } = factory;
        return Attempts.ok(create(this.#project.boxGraph, cellBox.instrument, defaultName, defaultIcon, attachment));
    }
    moveCompositeLayer(composite, fromIndex, toIndex) {
        const layers = IndexedBox.collectIndexedBoxes(composite.cells).slice();
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= layers.length || toIndex >= layers.length) {
            return;
        }
        const [moved] = layers.splice(fromIndex, 1);
        layers.splice(toIndex, 0, moved);
        layers.forEach((box, index) => box.index.setValue(index));
    }
    // re-hosts by pointer, nothing is copied: automation lanes keep their targets
    wrapInstrumentIntoComposite(instrumentBox) {
        const factoryKey = InstrumentFactories.keyOfBox(instrumentBox);
        if (!isDefined(factoryKey) || !InstrumentFactories.isLayerInstrument(InstrumentFactories.Named[factoryKey])) {
            return Attempts.err(`${instrumentBox.name} cannot be used as a layer`);
        }
        const hostBox = instrumentBox.host.targetVertex.unwrap("instrument.host").box;
        if (!isInstanceOf(hostBox, AudioUnitBox)) {
            return Attempts.err("The instrument is not hosted by an audio unit");
        }
        const { boxGraph } = this.#project;
        const { create, defaultIcon, defaultName } = InstrumentFactories.InstrumentComposite;
        const midiEffects = IndexedBox.collectIndexedBoxes(hostBox.midiEffects);
        const audioEffects = IndexedBox.collectIndexedBoxes(hostBox.audioEffects);
        instrumentBox.host.defer();
        const composite = create(boxGraph, hostBox.input, defaultName, defaultIcon);
        const cellBox = InstrumentCompositeCellBox.create(boxGraph, UUID.generate(), box => {
            box.composite.refer(composite.cells);
            box.index.setValue(0);
        });
        instrumentBox.host.refer(cellBox.instrument);
        this.moveEffects(cellBox.midiEffects, midiEffects, 0);
        this.moveEffects(cellBox.audioEffects, audioEffects, 0);
        return Attempts.ok({ cellBox, instrumentBox });
    }
    pasteAudioUnitAsLayer(composite, data, notes = "append") {
        return AudioUnitAsLayer.paste(this.#project, composite, data, notes);
    }
    // automation lanes are not copied
    duplicateCompositeLayer(cellBox) {
        const { boxGraph } = this.#project;
        const composite = asInstanceOf(cellBox.composite.targetVertex.unwrap("composite.target").box, InstrumentCompositeBox);
        const dependencies = TransferUtils.deviceDependencies(cellBox, box => box === composite);
        const uuidMap = TransferUtils.mapUuids([cellBox, ...dependencies]);
        const insertIndex = cellBox.index.getValue() + 1;
        IndexedBox.collectIndexedBoxes(composite.cells)
            .filter(box => box.index.getValue() >= insertIndex)
            .forEach(box => box.index.setValue(box.index.getValue() + 1));
        PointerField.decodeWith({
            map: (_pointer, address) => address.map(addr => uuidMap.opt(addr.uuid).mapOr(({ target }) => addr.moveTo(target), addr))
        }, () => TransferUtils.cloneBoxes([cellBox, ...dependencies]
            .filter(source => !TransferUtils.keepsIdentity(source)), uuidMap, boxGraph));
        const copy = asInstanceOf(boxGraph.findBox(uuidMap.get(cellBox.address.uuid, "cell copy").target)
            .unwrap("cell copy"), InstrumentCompositeCellBox);
        copy.index.setValue(insertIndex);
        return copy;
    }
    deleteCompositeLayer(cellBox) {
        const composite = asInstanceOf(cellBox.composite.targetVertex.unwrap("composite.target").box, InstrumentCompositeBox);
        const survivors = IndexedBox.collectIndexedBoxes(composite.cells).filter(box => box !== cellBox);
        cellBox.delete();
        survivors.forEach((box, index) => box.index.setValue(index));
    }
    insertEffect(field, factory, insertIndex = Number.MAX_SAFE_INTEGER) {
        return factory.create(this.#project, field, IndexedBox.insertOrder(field, insertIndex));
    }
    // Copies of the effects (with everything they own) placed in the target chain at the insert index.
    copyEffects(targetField, boxes, insertIndex) {
        if (boxes.length === 0) {
            return Arrays.empty();
        }
        const { boxGraph } = this.#project;
        const sorted = boxes.toSorted((left, right) => left.index.getValue() - right.index.getValue());
        const data = BoxGraphCopy.serializeBoxes([...sorted, ...DevicesClipboard.collectDeviceDependencies(sorted, boxGraph)]);
        const existing = IndexedBox.collectIndexedBoxes(targetField);
        const at = clamp(insertIndex, 0, existing.length);
        existing.forEach(box => {
            if (box.index.getValue() >= at) {
                box.index.setValue(box.index.getValue() + sorted.length);
            }
        });
        const copies = BoxGraphCopy.deserializeBoxes(data, boxGraph, {
            mapPointer: (pointer, address) => {
                if (address.isEmpty()) {
                    return Option.None;
                }
                if (pointer.pointerType === Pointers.MIDIEffectHost || pointer.pointerType === Pointers.AudioEffectHost) {
                    return Option.wrap(targetField.address);
                }
                return DevicesClipboard.mapModulationPointer(pointer, address, boxGraph);
            },
            keepUuid: isModulatorBox,
            excludeBox: box => DevicesClipboard.excludeExistingModulator(box, boxGraph)
        });
        const topLevel = copies
            .filter((box) => DeviceBoxUtils.isEffectDeviceBox(box)
            && box.host.targetVertex.mapOr(vertex => vertex === targetField, false))
            .toSorted((left, right) => left.index.getValue() - right.index.getValue());
        topLevel.forEach((box, index) => box.index.setValue(at + index));
        return topLevel;
    }
    moveEffects(targetField, boxes, insertIndex) {
        if (boxes.length === 0) {
            return;
        }
        const movedSet = new Set(boxes);
        // The chains the boxes currently live in, captured BEFORE re-homing so they can be reindexed afterwards.
        const sourceFields = new Set();
        boxes.forEach(box => box.host.targetVertex.ifSome(vertex => sourceFields.add(vertex)));
        const moved = boxes.slice().sort((left, right) => left.index.getValue() - right.index.getValue());
        const kept = IndexedBox.collectIndexedBoxes(targetField).filter(box => !movedSet.has(box));
        const at = clamp(insertIndex, 0, kept.length);
        const finalOrder = [...kept.slice(0, at), ...moved, ...kept.slice(at)];
        moved.forEach(box => box.host.refer(targetField));
        finalOrder.forEach((box, index) => box.index.setValue(index));
        sourceFields.forEach(field => {
            if (field === targetField) {
                return;
            }
            IndexedBox.collectIndexedBoxes(field).forEach((box, index) => box.index.setValue(index));
        });
    }
    createNoteTrack(audioUnitBox, insertIndex = Number.MAX_SAFE_INTEGER) {
        return this.#createTrack({ field: audioUnitBox.tracks, trackType: TrackType.Notes, insertIndex });
    }
    createAudioTrack(audioUnitBox, insertIndex = Number.MAX_SAFE_INTEGER) {
        return this.#createTrack({ field: audioUnitBox.tracks, trackType: TrackType.Audio, insertIndex });
    }
    createAutomationTrack(audioUnitBox, target, insertIndex = Number.MAX_SAFE_INTEGER) {
        return this.#createTrack({ field: audioUnitBox.tracks, target, trackType: TrackType.Value, insertIndex });
    }
    // Packs the audio unit's main tracks (Notes for MIDI units, Audio for audio
    // units) onto as few lanes as possible. Iterates tracks top-down; for each
    // region in a non-top track, scans the higher tracks left-to-right and moves
    // the region to the first one where it doesn't overlap an existing region.
    // Empty main tracks are then deleted, but at least one is kept; clips and
    // automation tracks are never moved or deleted.
    compactTracks(audioUnitBox) {
        const adapter = this.#project.boxAdapters.adapterFor(audioUnitBox, AudioUnitBoxAdapter);
        const inputAdapter = adapter.input.adapter();
        if (inputAdapter.isEmpty()) {
            return;
        }
        const accepts = inputAdapter.unwrap().accepts;
        if (accepts === false) {
            return;
        }
        const targetType = DeviceAccepts.toTrackType(accepts);
        const tracks = adapter.tracks.values()
            .filter(track => track.type === targetType)
            .toSorted((a, b) => a.indexField.getValue() - b.indexField.getValue());
        if (tracks.length < 2) {
            return;
        }
        // Reads regions live from the pointerHub (not from track.regions.collection),
        // because the cached collection isn't updated within the running transaction
        // and would miss regions just moved here in a previous iteration.
        const { boxAdapters } = this.#project;
        const fits = (track, position, complete) => RegionOverlap.hasSpace(boxAdapters, track.box, position, complete);
        for (let i = 1; i < tracks.length; i++) {
            // Snapshot the region list before mutating; moving via `refer` will
            // remove the region from this track's collection mid-iteration.
            const regions = [...tracks[i].box.regions.pointerHub.incoming().map(({ box }) => box)];
            for (const region of regions) {
                for (let j = 0; j < i; j++) {
                    const position = region.position.getValue();
                    const complete = RegionAdapters.for(boxAdapters, region).resolveComplete(position);
                    if (fits(tracks[j], position, complete)) {
                        region.regions.refer(tracks[j].box.regions);
                        break;
                    }
                }
            }
        }
        for (let i = tracks.length - 1; i >= 1; i--) {
            const track = tracks[i];
            if (track.box.regions.pointerHub.isEmpty() && track.box.clips.pointerHub.isEmpty()) {
                adapter.deleteTrack(track);
            }
        }
    }
    createTimeStretchedClip(props) {
        return AudioContentFactory.createTimeStretchedClip(props);
    }
    createTimeStretchedRegion(props) {
        return AudioContentFactory.createTimeStretchedRegion(props);
    }
    createPitchStretchedClip(props) {
        return AudioContentFactory.createPitchStretchedClip(props);
    }
    createPitchStretchedRegion(props) {
        return AudioContentFactory.createPitchStretchedRegion(props);
    }
    createNotStretchedClip(props) {
        return AudioContentFactory.createNotStretchedClip(props);
    }
    createNotStretchedRegion(props) {
        return AudioContentFactory.createNotStretchedRegion(props);
    }
    createNoteClip(trackBox, clipIndex, { name, hue } = {}) {
        const { boxGraph } = this.#project;
        const type = trackBox.type.getValue();
        if (type !== TrackType.Notes) {
            return panic("Incompatible track type for note-clip creation: " + type.toString());
        }
        const events = NoteEventCollectionBox.create(boxGraph, UUID.generate());
        return NoteClipBox.create(boxGraph, UUID.generate(), box => {
            box.index.setValue(clipIndex);
            box.label.setValue(name ?? "");
            box.hue.setValue(hue ?? ColorCodes.forTrackType(type));
            box.mute.setValue(false);
            box.duration.setValue(PPQN.Bar);
            box.clips.refer(trackBox.clips);
            box.events.refer(events.owners);
        });
    }
    duplicateRegion(region, options) {
        if (region.trackBoxAdapter.isEmpty()) {
            return Option.None;
        }
        const track = region.trackBoxAdapter.unwrap();
        const explicitPosition = options?.position;
        // `position` is Int32, but `complete` is fractional for regions with a float32 duration (musical audio
        // converted from seconds). Round UP, so the stored position is the one masked, never truncated into the
        // region before it (#1164).
        if (!isDefined(explicitPosition) && options?.findFreeSpace === true) {
            let insert = Math.ceil(region.complete);
            for (const { position, complete } of track.regions.collection.iterateFrom(region.complete)) {
                if (insert + region.duration <= position) {
                    break;
                }
                insert = Math.ceil(complete);
            }
            return Option.wrap(region.copyTo({
                position: insert,
                consolidate: true
            }));
        }
        const position = Math.ceil(explicitPosition ?? region.complete);
        const complete = position + region.duration;
        const targetTrack = this.#project.overlapResolver.resolveTargetTrack(track, position, complete);
        const solver = this.#project.overlapResolver.fromRange(targetTrack, position, complete);
        const duplicate = region.copyTo({
            position,
            target: targetTrack.box.regions,
            consolidate: true
        });
        solver();
        return Option.wrap(duplicate);
    }
    async exportMIDI(collection, suggestedName = "notes.mid") {
        return NoteMidiExport.toFile(collection, suggestedName);
    }
    async exportAudio(owner, suggestedName = "audio.wav") {
        return AudioWavExport.toFile(owner, suggestedName);
    }
    quantiseNotes(notes, { positionQuantisation, durationQuantisation, offset }) {
        if (isAbsent(positionQuantisation) && isAbsent(durationQuantisation)) {
            console.warn("Nothing to quantise: both quantisation parameters are absent");
            return;
        }
        const array = notes instanceof NoteEventCollectionBox
            ? notes.events.pointerHub.incoming().map(({ box }) => asInstanceOf(box, NoteEventBox))
            : notes;
        offset ??= 0.0;
        array.forEach(event => {
            let position = event.position.getValue();
            let duration = event.duration.getValue();
            if (isDefined(positionQuantisation)) {
                position = quantizeRound(position + offset, positionQuantisation) - offset;
            }
            if (isDefined(durationQuantisation)) {
                duration = Math.max(quantizeRound(duration, durationQuantisation), durationQuantisation);
            }
            event.position.setValue(Math.max(position, 0));
            event.duration.setValue(duration);
        });
    }
    createValueClip(trackBox, clipIndex, { name, hue } = {}) {
        const { boxGraph } = this.#project;
        const type = trackBox.type.getValue();
        if (type !== TrackType.Value) {
            return panic("Incompatible track type for value-clip creation: " + type.toString());
        }
        const events = ValueEventCollectionBox.create(boxGraph, UUID.generate());
        return ValueClipBox.create(boxGraph, UUID.generate(), box => {
            box.index.setValue(clipIndex);
            box.label.setValue(name ?? "");
            box.hue.setValue(hue ?? ColorCodes.forTrackType(type));
            box.mute.setValue(false);
            box.duration.setValue(PPQN.Bar);
            box.events.refer(events.owners);
            box.clips.refer(trackBox.clips);
        });
    }
    createNoteRegion({ trackBox, position, duration, loopOffset, loopDuration, eventCollection, mute, name, hue }) {
        if (trackBox.type.getValue() !== TrackType.Notes) {
            console.warn("You should not create a note-region in mismatched track");
        }
        const { boxGraph } = this.#project;
        const events = eventCollection ?? NoteEventCollectionBox.create(boxGraph, UUID.generate());
        return NoteRegionBox.create(boxGraph, UUID.generate(), box => {
            box.position.setValue(position);
            box.label.setValue(name ?? "");
            box.hue.setValue(hue ?? ColorCodes.forTrackType(trackBox.type.getValue()));
            box.mute.setValue(mute ?? false);
            box.duration.setValue(duration);
            box.loopOffset.setValue(loopOffset ?? 0);
            box.loopDuration.setValue(loopDuration ?? duration);
            box.events.refer(events.owners);
            box.regions.refer(trackBox.regions);
        });
    }
    createTrackRegion(trackBox, position, duration, { name, hue } = {}) {
        if (duration <= 0.0) {
            return Option.None;
        }
        const { boxGraph } = this.#project;
        const type = trackBox.type.getValue();
        const startPosition = Math.max(position, 0);
        // Resolve overlaps against existing regions BEFORE creating (mirrors duplicateRegion): drawing a region
        // over an existing one must clip / push / keep per the setting, never STACK a second region at the same
        // position. A raw create left two regions overlapping, which a later validateTracks hard-asserts on and
        // crashes the app (live errors 1086/1087).
        const trackAdapter = this.#project.boxAdapters.adapterFor(trackBox, TrackBoxAdapter);
        const solver = this.#project.overlapResolver.fromRange(trackAdapter, startPosition, startPosition + duration);
        const created = (() => {
            switch (type) {
                case TrackType.Notes: {
                    const events = NoteEventCollectionBox.create(boxGraph, UUID.generate());
                    return Option.wrap(NoteRegionBox.create(boxGraph, UUID.generate(), box => {
                        box.position.setValue(startPosition);
                        box.label.setValue(name ?? "");
                        box.hue.setValue(hue ?? ColorCodes.forTrackType(type));
                        box.mute.setValue(false);
                        box.duration.setValue(duration);
                        box.loopDuration.setValue(duration);
                        box.events.refer(events.owners);
                        box.regions.refer(trackBox.regions);
                    }));
                }
                case TrackType.Value: {
                    // #271: a new automation region inherits a single node from its surroundings — the preceding
                    // region's held (outgoing) value, else the following region's incoming value, else the
                    // parameter's current dial value. Computed BEFORE creating the region so the scan sees only
                    // the existing regions.
                    const seed = this.#automationSeed(trackBox, trackAdapter, startPosition);
                    const events = ValueEventCollectionBox.create(boxGraph, UUID.generate());
                    const region = ValueRegionBox.create(boxGraph, UUID.generate(), box => {
                        box.position.setValue(startPosition);
                        box.label.setValue(name ?? "");
                        box.hue.setValue(hue ?? ColorCodes.forTrackType(type));
                        box.mute.setValue(false);
                        box.duration.setValue(duration);
                        box.loopDuration.setValue(duration);
                        box.events.refer(events.owners);
                        box.regions.refer(trackBox.regions);
                    });
                    seed.ifSome(({ value, interpolation }) => ValueEventBox.create(boxGraph, UUID.generate(), box => {
                        box.position.setValue(0);
                        box.value.setValue(value);
                        box.events.refer(events.events);
                        InterpolationFieldAdapter.write(box.interpolation, interpolation);
                    }));
                    return Option.wrap(region);
                }
            }
            return Option.None;
        })();
        if (created.nonEmpty()) {
            solver();
        }
        return created;
    }
    // #271: the node a freshly drawn automation region should hold. Hold-from-left: the preceding region's
    // outgoing (held) value wins; else the following region's incoming value; else the parameter's current dial
    // value. Returns None only when the track's target parameter cannot be resolved (a bug — seed no node then).
    #automationSeed(trackBox, trackAdapter, position) {
        return trackBox.target.targetVertex
            .flatMap(vertex => this.#project.parameterFieldAdapters.opt(vertex.address))
            .map(parameter => {
            const dial = parameter.getControlledUnitValue();
            const interpolation = InterpolationFieldAdapter.map(parameter.type);
            const preceding = trackAdapter.regions.collection.lowerEqual(position);
            if (isDefined(preceding) && preceding.isValueRegion()) {
                return { value: preceding.outgoingValue(dial), interpolation };
            }
            const following = trackAdapter.regions.collection.greaterEqual(position);
            if (isDefined(following) && following.isValueRegion()) {
                return { value: following.incomingValue(dial), interpolation };
            }
            return { value: dial, interpolation };
        });
    }
    createNoteEvent({ owner, position, duration, velocity, pitch, chance, cent }) {
        const { boxGraph } = this.#project;
        return NoteEventBox.create(boxGraph, UUID.generate(), box => {
            box.position.setValue(position);
            box.duration.setValue(duration);
            box.velocity.setValue(velocity ?? 1.0);
            box.pitch.setValue(pitch);
            box.chance.setValue(chance ?? 100.0);
            box.cent.setValue(cent ?? 0.0);
            box.events.refer(owner.events.targetVertex
                .unwrap("Owner has no event-collection").box
                .asBox(NoteEventCollectionBox).events);
        });
    }
    deleteAudioUnit(audioUnitBox) {
        // The output unit is mandatory; deleting it desyncs the engine (it rejects the transaction).
        if (audioUnitBox.type.getValue() === AudioUnitType.Output) {
            return;
        }
        const { rootBox } = this.#project;
        IndexedBox.removeOrder(rootBox.audioUnits, audioUnitBox.index.getValue());
        audioUnitBox.delete();
    }
    /**
     * Duplicate a set of notes so that the copies land flush after the
     * source block: each copy is shifted by `max(position + duration) −
     * min(position)` over the input. Returns the newly created note
     * adapters in the same order as `notes`, so the caller can swap its
     * selection in one pass. Returns an empty array when the input is
     * empty or the computed shift is zero. The caller is responsible for
     * wrapping the call in `editing.modify(...)`.
     */
    duplicateNotes(notes) {
        if (notes.length === 0) {
            return [];
        }
        const blockStart = notes.reduce((min, { position }) => Math.min(min, position), Infinity);
        const blockEnd = notes.reduce((max, { position, duration }) => Math.max(max, position + duration), -Infinity);
        const shift = blockEnd - blockStart;
        if (shift <= 0) {
            return [];
        }
        return notes.map(adapter => adapter.copyTo({ position: adapter.position + shift }));
    }
    #createTrack({ field, target, trackType, insertIndex }) {
        const index = IndexedBox.insertOrder(field, insertIndex);
        return TrackBox.create(this.#project.boxGraph, UUID.generate(), box => {
            box.index.setValue(index);
            box.type.setValue(trackType);
            box.tracks.refer(field);
            box.target.refer(target ?? field.box);
        });
    }
    #trackTypeToCapture(boxGraph, trackType) {
        switch (trackType) {
            case TrackType.Audio:
                return Option.wrap(CaptureAudioBox.create(boxGraph, UUID.generate()));
            case TrackType.Notes:
                return Option.wrap(CaptureMidiBox.create(boxGraph, UUID.generate()));
            default:
                return Option.None;
        }
    }
}
